import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import {
  Mail, Folder, UserPlus, Zap, Calculator, ArrowLeftRight, FileText,
  AlarmClock, TrendingUp, MessageCircle, Library, Link2,
} from "lucide-react";

// Tile catalog — mirrors the LoanSupportIndia mobile app's Quick Actions exactly
// (label, icon and colour). `to` navigates; `soon` shows a coming-soon toast for
// app screens not yet built on the web.
const T = {
  queries: { label: "Queries", icon: Mail, color: "text-amber-600", bg: "bg-amber-100 dark:bg-amber-950/40", to: "/queries" },
  coord: { label: "Coord Files", icon: Folder, color: "text-emerald-600", bg: "bg-emerald-100 dark:bg-emerald-950/40", to: "/coord-files" },
  addLead: { label: "Add Lead", icon: UserPlus, color: "text-blue-600", bg: "bg-blue-100 dark:bg-blue-950/40", to: "/leads" },
  compare: { label: "Compare Banks", icon: Zap, color: "text-white", bg: "bg-slate-900", to: "/tools?tab=multibank" },
  emi: { label: "EMI Calc", icon: Calculator, color: "text-emerald-600", bg: "bg-emerald-100 dark:bg-emerald-950/40", to: "/tools?tab=emi" },
  bt: { label: "Balance Transfer", icon: ArrowLeftRight, color: "text-teal-600", bg: "bg-teal-100 dark:bg-teal-950/40", soon: true },
  apps: { label: "Applications", icon: FileText, color: "text-amber-600", bg: "bg-amber-100 dark:bg-amber-950/40", to: "/applications" },
  followups: { label: "Follow-ups", icon: AlarmClock, color: "text-rose-600", bg: "bg-rose-100 dark:bg-rose-950/40", to: "/followups" },
  analytics: { label: "Analytics", icon: TrendingUp, color: "text-violet-600", bg: "bg-violet-100 dark:bg-violet-950/40", to: "/analytics" },
  messages: { label: "Messages", icon: MessageCircle, color: "text-blue-600", bg: "bg-blue-100 dark:bg-blue-950/40", soon: true },
  notes: { label: "Bank Notes", icon: Library, color: "text-violet-600", bg: "bg-violet-100 dark:bg-violet-950/40", to: "/bank-notes" },
  links: { label: "Important Links", icon: Link2, color: "text-blue-600", bg: "bg-blue-100 dark:bg-blue-950/40", adminTo: "/settings?tab=links", soon: true },
};

// Per-role ordered layouts, exactly as the mobile app renders them.
const FULL = ["queries", "coord", "addLead", "compare", "emi", "bt", "apps", "followups", "analytics", "messages", "notes", "links"];
const TELECALLER = ["queries", "addLead", "compare", "emi", "bt", "apps", "followups", "analytics", "messages", "notes", "links"];

const testid = (label) => `quick-action-${label.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "")}`;

export function QuickActions() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const role = user?.role;
  const isAdmin = role === "Admin";
  const keys = role === "Telecaller" ? TELECALLER : FULL;

  const handle = (key, tile) => {
    if (key === "addLead" && role === "Coordinator") return; // disabled for coordinator (matches app)
    if (key === "links" && isAdmin) return navigate(tile.adminTo);
    if (tile.to) return navigate(tile.to);
    toast.info(`${tile.label} — coming soon on the web`);
  };

  return (
    <div className="mt-6" data-testid="quick-actions">
      <h3 className="font-heading font-semibold text-slate-800 dark:text-slate-200 mb-3">Quick Actions</h3>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
        {keys.map((key) => {
          const tile = T[key];
          const disabled = key === "addLead" && role === "Coordinator";
          return (
            <Card
              key={key}
              onClick={() => !disabled && handle(key, tile)}
              data-testid={testid(tile.label)}
              className={`p-4 border-slate-200/80 dark:border-slate-800/80 transition-all ${disabled ? "opacity-40 cursor-not-allowed" : "cursor-pointer hover:border-blue-300 dark:hover:border-blue-700 hover:shadow-md"}`}
            >
              <div className={`h-10 w-10 rounded-lg ${tile.bg} flex items-center justify-center mb-3`}><tile.icon className={`h-5 w-5 ${tile.color}`} /></div>
              <div className="font-medium text-sm text-slate-900 dark:text-slate-100">{tile.label}</div>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
